import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2, Ban, Copy, HelpCircle, Eye, MessageCircle, XCircle } from "lucide-react";

const RESUMEN_CARDS = [
  { key: "descartar_no_reserva", label: "Avisos/no-reservas a descartar", icon: Ban, cls: "text-rose-700 bg-rose-50 border-rose-200" },
  { key: "mensaje_sin_datos", label: "Mensajes sin datos → Acciones", icon: MessageCircle, cls: "text-sky-700 bg-sky-50 border-sky-200" },
  { key: "auto_confirmar", label: "Se auto-confirmarían", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "cancelacion_detectada", label: "Cancelaciones mal cargadas", icon: XCircle, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "duplicado_bokun", label: "Duplicados de Bókun a descartar", icon: Copy, cls: "text-violet-700 bg-violet-50 border-violet-200" },
  { key: "ambiguo", label: "Quedan realmente ambiguas", icon: HelpCircle, cls: "text-slate-700 bg-slate-100 border-slate-200" },
];

export default function LimpiezaBandejaDialog({ open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [respPreview, setRespPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const cargarPreview = async () => {
    setLoading(true);
    setPreview(null);
    setRespPreview(null);
    try {
      const [b, r] = await Promise.all([
        api.get("/captura/bandeja/vista-previa"),
        api.get("/respuestas/limpieza/vista-previa"),
      ]);
      setPreview(b.data);
      setRespPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
  };

  useEffect(() => {
    if (open) cargarPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const aplicar = async () => {
    setApplying(true);
    try {
      const [b, r] = await Promise.all([
        api.post("/captura/bandeja/aplicar"),
        api.post("/respuestas/limpieza/aplicar"),
      ]);
      toast.success(
        `${b.data.auto_confirmadas} confirmada(s) · ${b.data.canceladas_por_cancelacion} cancelación(es) corregida(s) · ` +
        `${b.data.descartadas_no_reserva} aviso(s) descartado(s) · ${b.data.mensajes_sin_datos_movidos} mensaje(s) movido(s) a Acciones · ` +
        `${b.data.duplicados_descartados} duplicado(s) de Bókun descartado(s) · ${r.data.descartadas} borrador(es) redundante(s) descartado(s).`
      );
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const total = preview?.total ?? 0;
  const accionables = (preview?.auto_confirmar || 0) + (preview?.cancelacion_detectada || 0) +
    (preview?.duplicado_bokun || 0) + (preview?.descartar_no_reserva || 0) + (preview?.mensaje_sin_datos || 0);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl" data-testid="limpieza-bandeja-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> Limpiar Bandeja acumulada
          </DialogTitle>
          <DialogDescription>
            Reprocesa TODA la Bandeja con las reglas de clasificación más recientes (descarta avisos
            que no son reservas, mueve mensajes sin datos a Acciones, auto-confirma lo completo y
            aplica cancelaciones detectadas) -- vista previa ANTES de aplicar nada.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : preview ? (
          <div className="space-y-3" data-testid="limpieza-bandeja-resumen">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {RESUMEN_CARDS.map(({ key, label, icon: Icon, cls }) => (
                <Card key={key} className={`p-3 border ${cls}`} data-testid={`limpieza-card-${key}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium">
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </div>
                  <p className="text-2xl font-bold mt-1">{preview[key] || 0}</p>
                </Card>
              ))}
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> {total} reserva(s) en Bandeja hoy · {accionables} se resolverían solas
              al aplicar.
            </p>
            {respPreview && (
              <p className="text-xs text-slate-500 border-t border-slate-100 pt-2" data-testid="limpieza-respuestas-resumen">
                Borradores de "Confirmación de reserva": <strong>{respPreview.redundantes}</strong> son
                redundantes (agencia/OTA que ya confirma) de {respPreview.total} totales. Se mantienen{" "}
                {respPreview.a_mantener} (ventas directas).
              </p>
            )}
            {total === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No hay nada pendiente en la Bandeja. ¡Todo al día!</p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="limpieza-bandeja-cancelar">
            Cancelar
          </Button>
          <Button
            onClick={aplicar}
            disabled={applying || loading || (accionables === 0 && (respPreview?.redundantes || 0) === 0)}
            data-testid="limpieza-bandeja-aplicar"
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Aplicar limpieza`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
